import { Component, OnInit, signal, computed, ElementRef, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TranslatePipe } from '@ngx-translate/core';
import { BreadcrumbService } from './breadcrumb.service';
import { CommonModule } from '@angular/common';
import { Popover } from 'primeng/popover';
import { PopoverModule } from 'primeng/popover';
import { ButtonModule } from 'primeng/button';

@Component({
  selector: 'gate-breadcrumb',
  standalone: true,
  imports: [BreadcrumbModule, TranslatePipe, RouterLink, CommonModule, PopoverModule, ButtonModule],
  templateUrl: './breadcrumb.component.html',
  styles:`
  :host {
    display: block;
    min-width: 0;
    width: 100%;
  }
  ::ng-deep{
    .p-breadcrumb .p-breadcrumb-item:last-child a,
    .p-breadcrumb .p-breadcrumb-item:last-child p {
      max-width: 100% !important
    }
    .ellipsis-item {
      cursor: pointer;
      color: var(--p-text-color);
      transition: color 0.2s;
      user-select: none;
      font-weight: bold;
    }
    .ellipsis-item:hover {
      color: var(--p-primary-color);
    }
    .p-breadcrumb {
      background: transparent !important;
      border: none;
      padding: 0;
    }
  }`
})
export class BreadcrumbComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('breadcrumbContainer', { static: false }) breadcrumbContainer?: ElementRef;
  @ViewChild('popover') popover?: Popover;

  breadcrumbItems = signal<MenuItem[] | undefined>(undefined);
  isExpanded = signal<boolean>(false);
  containerWidth = signal<number>(0);
  itemsToHide = signal<number>(0);
  hiddenItems = computed(() => {
    const items = this.breadcrumbItems();
    const hideCount = this.itemsToHide();
    if (!items || hideCount === 0) return [];

    const visibleCount = items.length - hideCount;

    // When visibleCount <= 1, displayedBreadcrumbs shows only the last item
    // So hidden items are all items except the last one
    if (visibleCount <= 1) {
      return items.slice(0, items.length - 1);
    }

    const itemsForLast = Math.min(2, Math.floor(visibleCount / 2));
    const itemsForFirst = Math.max(1, visibleCount - itemsForLast - 1);
    return items.slice(itemsForFirst, items.length - itemsForLast);
  });

  private resizeObserver?: ResizeObserver;
  private measureTimeout?: any;
  private isMeasuring = false;
  private hideTimeout?: any;

  home: MenuItem = { icon: 'pi pi-home', routerLink: '/home' };

  // Computed property for displayed breadcrumbs
  displayedBreadcrumbs = computed(() => {
    const items = this.breadcrumbItems();
    const hideCount = this.itemsToHide();

    if (!items || items.length === 0) {
      return items;
    }

    // If expanded, show all items
    if (this.isExpanded()) {
      return items;
    }

    // If nothing to hide, show all
    if (hideCount === 0) {
      return items;
    }

    // Calculate visible items
    const visibleCount = items.length - hideCount;

    // Very tight space - show ellipsis + last item only
    if (visibleCount <= 1) {
      const ellipsisItem: MenuItem = {
        label: '...',
        title: `Click to view`,
        styleClass: 'ellipsis-item'
      };
      return [ellipsisItem, items[items.length - 1]];
    }

    // Show first item(s) + ellipsis + last item(s)
    const itemsForLast = Math.min(2, Math.floor(visibleCount / 2));
    const itemsForFirst = Math.max(1, visibleCount - itemsForLast - 1); // -1 for ellipsis

    const first = items.slice(0, itemsForFirst);
    const last = items.slice(-itemsForLast);

    const ellipsisItem: MenuItem = {
      label: '...',
      title: `Click to view`,
      styleClass: 'ellipsis-item'
    };

    return [...first, ellipsisItem, ...last];
  });

  constructor(
    private breadcrumbService: BreadcrumbService,
  ) {}

  ngOnInit() {
    this.breadcrumbService.breadcrumbItems$.subscribe(breadcrumbs => {
      this.breadcrumbItems.set(breadcrumbs);
      this.isExpanded.set(false);
      this.scheduleMeasurement();
    });
  }

  ngAfterViewInit() {
    if (this.breadcrumbContainer) {
      this.updateWidth();

      this.resizeObserver = new ResizeObserver(() => {
        this.updateWidth();
        this.scheduleMeasurement();
      });

      this.resizeObserver.observe(this.breadcrumbContainer.nativeElement);
      this.scheduleMeasurement();
    }
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.measureTimeout) {
      clearTimeout(this.measureTimeout);
    }
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
  }

  toggleExpand() {
    this.isExpanded.update(val => !val);
  }

  showOverlay(event: Event) {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
    this.popover?.show(event);
  }

  hideOverlay() {
    this.hideTimeout = setTimeout(() => {
      this.popover?.hide();
    }, 200);
  }

  keepOverlayOpen() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
  }

  onOverlayHide() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
  }

  private updateWidth() {
    if (this.breadcrumbContainer) {
      const width = this.breadcrumbContainer.nativeElement.offsetWidth;
      this.containerWidth.set(width);
    }
  }

  private scheduleMeasurement() {
    if (this.isMeasuring) {
      return;
    }

    if (this.measureTimeout) {
      clearTimeout(this.measureTimeout);
    }

    this.measureTimeout = setTimeout(() => {
      this.measureAndCollapse();
    }, 100);
  }

  private measureAndCollapse() {
    const items = this.breadcrumbItems();
    const width = this.containerWidth();

    if (!items || items.length === 0 || width === 0 || !this.breadcrumbContainer || this.isMeasuring) {
      return;
    }

    this.isMeasuring = true;

    // First, show all items
    this.itemsToHide.set(0);

    // Wait for render
    setTimeout(() => {
      const breadcrumbEl = this.breadcrumbContainer?.nativeElement.querySelector('.p-breadcrumb');
      if (!breadcrumbEl) {
        this.isMeasuring = false;
        return;
      }

      const scrollWidth = breadcrumbEl.scrollWidth;
      const containerWidth = this.containerWidth();

      // If everything fits, we're done
      if (scrollWidth <= containerWidth) {
        this.itemsToHide.set(0);
        this.isMeasuring = false;
        return;
      }

      // Need to hide items - try hiding progressively
      this.findOptimalHideCount(1, items.length - 1, containerWidth);
    }, 50);
  }

  private findOptimalHideCount(hideCount: number, maxHide: number, targetWidth: number) {
    if (hideCount > maxHide) {
      this.itemsToHide.set(maxHide);
      this.isMeasuring = false;
      return;
    }

    this.itemsToHide.set(hideCount);

    setTimeout(() => {
      const breadcrumbEl = this.breadcrumbContainer?.nativeElement.querySelector('.p-breadcrumb');
      if (!breadcrumbEl) {
        this.isMeasuring = false;
        return;
      }

      const scrollWidth = breadcrumbEl.scrollWidth;

      if (scrollWidth <= targetWidth) {
        // Found the optimal count
        this.isMeasuring = false;
        return;
      } else {
        // Need to hide more
        this.findOptimalHideCount(hideCount + 1, maxHide, targetWidth);
      }
    }, 30);
  }
}
